import { serializeFilters, type QueryFilters } from '../core/queryKeyUtils';

export const salesKeys = {
  all: ['sales'] as const,
  bundle: {
    homeScope: () => [...salesKeys.all, 'bundle', 'home'] as const,
    home: (filters?: QueryFilters) =>
      [...salesKeys.bundle.homeScope(), serializeFilters(filters)] as const,
    pipelineScope: () => [...salesKeys.all, 'bundle', 'pipeline'] as const,
    pipeline: (filters?: QueryFilters) =>
      [...salesKeys.bundle.pipelineScope(), serializeFilters(filters)] as const,
    leadScope: (leadId: number) => [...salesKeys.all, 'bundle', 'lead', leadId] as const,
    lead: (leadId: number, filters?: QueryFilters) =>
      [...salesKeys.bundle.leadScope(leadId), serializeFilters(filters)] as const,
    managerScope: () => [...salesKeys.all, 'bundle', 'manager'] as const,
    manager: (filters?: QueryFilters) =>
      [...salesKeys.bundle.managerScope(), serializeFilters(filters)] as const,
    managerAssignmentsScope: () => [...salesKeys.all, 'bundle', 'manager-assignments'] as const,
    managerAssignments: (filters?: QueryFilters) =>
      [...salesKeys.bundle.managerAssignmentsScope(), serializeFilters(filters)] as const,
    performance: () => [...salesKeys.all, 'bundle', 'performance'] as const,
    tasksScope: () => [...salesKeys.all, 'bundle', 'tasks'] as const,
    tasks: (filters?: QueryFilters) =>
      [...salesKeys.bundle.tasksScope(), serializeFilters(filters)] as const,
    historyScope: () => [...salesKeys.all, 'bundle', 'history'] as const,
    history: (filters?: QueryFilters) =>
      [...salesKeys.bundle.historyScope(), serializeFilters(filters)] as const,
  },
  dashboard: (filters?: QueryFilters) =>
    [...salesKeys.all, 'dashboard', serializeFilters(filters)] as const,
  performanceDetails: (
    employeeId: number | null,
    dateFrom?: string,
    dateTo?: string,
  ) =>
    [...salesKeys.all, 'performance-details', employeeId ?? 'none', dateFrom ?? 'none', dateTo ?? 'none'] as const,
  leadsByStatus: (
    employeeId: number | null,
    statuses: string[] | null,
    filters?: QueryFilters,
  ) =>
    [
      ...salesKeys.all,
      'leads-by-status',
      employeeId ?? 'none',
      serializeFilters({
        ...(filters || {}),
        status_filters: Array.isArray(statuses) && statuses.length ? statuses.join(',') : undefined,
      }),
    ] as const,
  pipeline: {
    all: () => [...salesKeys.all, 'pipeline'] as const,
    list: (filters?: QueryFilters) =>
      [...salesKeys.pipeline.all(), 'list', serializeFilters(filters)] as const,
    lead: (leadId: number) => [...salesKeys.pipeline.all(), 'lead', leadId] as const,
    filters: () => [...salesKeys.pipeline.all(), 'filters'] as const,
    employees: () => [...salesKeys.pipeline.all(), 'employees'] as const,
    campaignDetails: () => [...salesKeys.pipeline.all(), 'campaign-details'] as const,
    completionQueue: (employeeId?: number, pageSize = 25) =>
      [...salesKeys.pipeline.all(), 'completion-queue', employeeId ?? 'all', pageSize] as const,
  },
  tasks: {
    all: () => [...salesKeys.all, 'tasks'] as const,
    detail: (filters?: QueryFilters) =>
      [...salesKeys.tasks.all(), serializeFilters(filters)] as const,
  },
  history: {
    all: () => [...salesKeys.all, 'history'] as const,
    list: (filters?: QueryFilters) =>
      [...salesKeys.history.all(), 'list', serializeFilters(filters)] as const,
    stats: (filters?: QueryFilters) =>
      [...salesKeys.history.all(), 'stats', serializeFilters(filters)] as const,
    employees: () => [...salesKeys.history.all(), 'employees'] as const,
  },
  leads: {
    all: () => [...salesKeys.all, 'leads'] as const,
    detail: (leadId: number) => [...salesKeys.leads.all(), leadId] as const,
    history: (leadId: number, cursor?: string | null) =>
      [...salesKeys.leads.detail(leadId), 'history', cursor ?? 'initial'] as const,
    availableCourses: (leadId: number) =>
      [...salesKeys.leads.detail(leadId), 'available-courses'] as const,
    cycle: (leadId: number) => [...salesKeys.leads.detail(leadId), 'cycle'] as const,
  },
  cycle: {
    consultationSlots: (courseName?: string) =>
      [...salesKeys.all, 'cycle', 'consultation-slots', courseName?.trim() || 'all'] as const,
  },
  courses: () => [...salesKeys.all, 'courses'] as const,
  courseAssets: () => [...salesKeys.all, 'course-assets'] as const,
  chat: {
    all: () => [...salesKeys.all, 'chat'] as const,
    conversations: (query: string, limit: number) =>
      [...salesKeys.chat.all(), 'conversations', serializeFilters({ query, limit })] as const,
    messages: (leadId: number, limit = 50) =>
      [...salesKeys.chat.all(), 'messages', leadId, limit] as const,
    leadDetail: (leadId: number) => [...salesKeys.chat.all(), 'lead', leadId] as const,
  },
  targets: {
    all: () => [...salesKeys.all, 'targets'] as const,
    list: (month?: string) => [...salesKeys.targets.all(), 'list', month ?? 'all'] as const,
    progress: (month?: string) => [...salesKeys.targets.all(), 'progress', month ?? 'current'] as const,
    company: (month?: string) => [...salesKeys.targets.all(), 'company', month ?? 'current'] as const,
    companyBreakdown: (month?: string) =>
      [...salesKeys.targets.all(), 'company-breakdown', month ?? 'current'] as const,
    history: () => [...salesKeys.targets.all(), 'history'] as const,
    breakdown: (employeeId: number, month?: string) =>
      [...salesKeys.targets.all(), 'breakdown', employeeId, month ?? 'current'] as const,
    competitions: () => [...salesKeys.targets.all(), 'competitions'] as const,
    competitionCurrent: () => [...salesKeys.targets.all(), 'competition-current'] as const,
    competitionScope: () => [...salesKeys.targets.all(), 'competition'] as const,
    competitionById: (competitionId: number | null) =>
      [...salesKeys.targets.competitionScope(), competitionId ?? 'none'] as const,
    bonuses: (employeeId?: number) =>
      [...salesKeys.targets.all(), 'bonuses', employeeId ?? 'all'] as const,
    policyOverview: (month?: string, employeeId?: number | null) =>
      [...salesKeys.targets.all(), 'policy-overview', month ?? 'current', employeeId ?? 'all'] as const,
    policyReview: (month?: string, employeeId?: number | null) =>
      [...salesKeys.targets.all(), 'policy-review', month ?? 'current', employeeId ?? 'all'] as const,
    penalties: (month?: string, employeeId?: number | null, penaltyStatus?: string | null) =>
      [...salesKeys.targets.all(), 'penalties', month ?? 'current', employeeId ?? 'all', penaltyStatus ?? 'all'] as const,
    commissionTiers: (month?: string) =>
      [...salesKeys.targets.all(), 'commission-tiers', month ?? 'current'] as const,
    calculatedBonuses: (month?: string) =>
      [...salesKeys.targets.all(), 'calculated-bonuses', month ?? 'current'] as const,
  },
  reports: {
    all: () => [...salesKeys.all, 'reports'] as const,
    teamSales: (month?: string) =>
      [...salesKeys.reports.all(), 'team-sales', month ?? 'current'] as const,
    targetAnalytics: (month?: string) =>
      [...salesKeys.reports.all(), 'target-analytics', month ?? 'current'] as const,
  },
} as const;
